"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  applyCustomerDetailReplicaGuard,
  customerDetailReplicaGuardIsExpired,
  customerDetailReplicaGuardIsSatisfied,
  customerDetailReplicaGuardQueryKey,
  CustomerDetailReplicaGuard,
} from "@/dashboard/features/hub/hooks/customer/customer-detail-replica-guard";
import { HUB_POLL_INTERVALS_MS } from "@/dashboard/features/hub/lib/constants";
import { CustomerDetail } from "@/dashboard/features/hub/lib/customer/schemas";
import { useHubPollInterval } from "@/dashboard/features/hub/realtime/hub-realtime-status";
import { fetchJson } from "@/dashboard/hooks/api/fetch-json";
import { useTenantKeyFromTuple, useTenantQuery } from "@/dashboard/hooks/query/use-tenant-query";

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

export function fetchCustomerDetail(id: string) {
  return fetchJson<CustomerDetail>(`/api/dashboard/customers/${id}`);
}

/* ------------------------------------------------------------------ */
/* Key builders                                                       */
/* ------------------------------------------------------------------ */

export const CUSTOMER_DETAIL_QUERY_KEY_PREFIX = "customer" as const;

export const customerDetailQueryKey = (id: string) =>
  [CUSTOMER_DETAIL_QUERY_KEY_PREFIX, id] as const;

/* ------------------------------------------------------------------ */
/* Hook                                                               */
/* ------------------------------------------------------------------ */

export function useCustomerDetailQuery(id: string, initial?: CustomerDetail) {
  const queryClient = useQueryClient();
  const tk = useTenantKeyFromTuple();
  const guardKey = useMemo(() => tk(customerDetailReplicaGuardQueryKey(id)), [id, tk]);
  const refetchInterval = useHubPollInterval(HUB_POLL_INTERVALS_MS.customerDetail);

  return useTenantQuery<CustomerDetail>({
    queryKey: customerDetailQueryKey(id),
    queryFn: async () => {
      const customer = await fetchCustomerDetail(id);
      const guard = queryClient.getQueryData<CustomerDetailReplicaGuard>(guardKey);
      if (
        guard &&
        (customerDetailReplicaGuardIsExpired(guard) ||
          customerDetailReplicaGuardIsSatisfied(customer, guard))
      ) {
        queryClient.removeQueries({ queryKey: guardKey, exact: true });
      }
      return applyCustomerDetailReplicaGuard(customer, guard);
    },
    initialData: initial,
    staleTime: 10_000,
    refetchInterval,
  });
}
